import { Input } from '@HoudiniUI/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@HoudiniUI/Select';
import {
	UserMapMappedStatus,
	invalidateInfiniteUserMapsQuery,
	useAdminAccountQuery,
	useGetExternalIntegrationUserMapConnectionTypesQuery,
	useInfiniteExternalIntegrationUserMapsQuery,
	useRegisterUserMutation,
	useUserOperationsQuery,
} from '@Queries/index';
import { warningDark } from '@Styles/colors';
import { baseStyleSheet } from '@Styles/styles';
import * as Api from '@ViewModels';
import { AutoCompleteSearchFieldAccessoryViewMode } from '@WebComponents/autocomplete/AutoCompleteSearchField';
import { ISimpleAutoCompleteSearchFieldItemSelectionEvent } from '@WebComponents/autocomplete/SimpleAutoCompleteSearchField';
import { Button } from '@HoudiniUI/Button';
import {
	DataGrid,
	DataGridColumn,
	DataGridColumns,
	DataGridRow,
	DataGridRowItem,
	DataGridRows,
} from '@WebComponents/DataGrid';
import { Badge } from '@WebComponents/dataBoards/Badge';
import { LoadingSpinner } from '@WebComponents/LoadingSpinner';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useDebounceValue } from '../../../../../../../hooks/useDebounceValue';
import { IAddEmployeeResult } from '../../../../../../../models/AdminModels';
import { getDisplayName } from '../../../../../../../models/NameUtils';
import { AddEmployeeModal } from '../../../../../../components/user/AddEmployee';
import { isNorthwesternMutualBrokerDealer } from '../../../../../../utils';
import { ImportUserMapsModal } from './ImportUserMapsModal';
import { SettingGroup } from '../SettingsView/components/SettingGroup';
import { EditModeButton, EditModeSimpleAutoCompleteSearchField } from '../SettingsView/context';
import { styleSheet } from './styles';

interface IPendingUserMapEdit {
	newUserId: string | null;
	baseline: string | null;
}

interface IUserMappingsViewProps {
	impersonationContext: Api.IImpersonationContext;
	integrationProvider: Api.IntegrationProvider;
	/**
	 * Keyed by providerId. Each entry stores the new userId plus the baseline (server
	 * value the user saw on first edit). Reverts compare against the baseline so a
	 * concurrent admin's edit isn't silently re-applied on toggle-back.
	 */
	pendingUserMapEdits: Map<string, IPendingUserMapEdit>;
	setPendingUserMapEdit: (providerId: string, newUserId: string | null, currentServerUserId: string | null) => void;
	dropPendingUserMapEdit: (providerId: string) => void;
}

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
const USER_OPERATIONS_LOOKUP_LIMIT = 1000;

const mappedStatusOptions: { label: string; value: UserMapMappedStatus }[] = [
	{ label: 'All Statuses', value: UserMapMappedStatus.All },
	{ label: 'Mapped', value: UserMapMappedStatus.Mapped },
	{ label: 'Unmapped', value: UserMapMappedStatus.Unmapped },
];

export const UserMappingsView: React.FC<IUserMappingsViewProps> = ({
	impersonationContext,
	integrationProvider,
	pendingUserMapEdits,
	setPendingUserMapEdit,
	dropPendingUserMapEdit,
}) => {
	const [searchInput, setSearchInput] = React.useState('');
	const debouncedSearch = useDebounceValue(searchInput, SEARCH_DEBOUNCE_MS);
	const [connectionType, setConnectionType] = React.useState<string>('all');
	const [mappedStatus, setMappedStatus] = React.useState<UserMapMappedStatus>(UserMapMappedStatus.All);
	const [showAddEmployee, setShowAddEmployee] = React.useState(false);
	const [showImportUserMaps, setShowImportUserMaps] = React.useState(false);

	const userMapsQuery = useInfiniteExternalIntegrationUserMapsQuery({
		impersonationContext,
		integrationProvider,
		pageSize: PAGE_SIZE,
		filter: {
			search: debouncedSearch.trim() || undefined,
			connectionType: connectionType === 'all' ? undefined : connectionType,
			mappedStatus,
		},
	});

	const connectionTypesQuery = useGetExternalIntegrationUserMapConnectionTypesQuery({
		impersonationContext,
		integrationProvider,
	});

	// Bump well above useUserOperationsQuery's default 100-row cap. Accounts that need this
	// paginated user-maps view typically also have many Levitate users; without this, rows
	// whose userId isn't in the first 100 would render as "Unknown user". A batched lookup
	// keyed by visible row userIds would be cleaner long-term.
	const userOperationsQuery = useUserOperationsQuery({
		accountId: impersonationContext.account.id,
		pageSize: USER_OPERATIONS_LOOKUP_LIMIT,
	});

	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
		enabled: integrationProvider === Api.IntegrationProvider.MicrosoftDynamics365Sales,
	});

	const registerUserMutation = useRegisterUserMutation({
		onSuccess: () => toast.success('User added successfully'),
	});

	const accountUsersById = React.useMemo(() => {
		const values = (userOperationsQuery.data?.values ?? []) as Api.IUserWithStats[];
		return new Map(values.map(u => [u.id, u]));
	}, [userOperationsQuery.data]);

	const connectionTypeOptions = React.useMemo(
		() => [
			{ value: 'all', label: 'All Types' },
			...(connectionTypesQuery.data ?? []).map(type => ({ value: type, label: type })),
		],
		[connectionTypesQuery.data]
	);

	const queryPages = userMapsQuery.data?.pages;
	const allRows = React.useMemo<Api.IIntegrationUser[]>(() => queryPages?.flatMap(p => p.values) ?? [], [queryPages]);
	const totalCount = queryPages?.[0]?.totalCount ?? 0;
	const loadedCount = allRows.length;
	const isInitialLoading = userMapsQuery.isPending || userOperationsQuery.isPending;
	const hasNextPage = userMapsQuery.hasNextPage;
	const pendingEditCount = pendingUserMapEdits.size;

	// When a background refetch lands a server value that matches a pending edit,
	// the entry is a no-op. Drop it so the footer count, modified-row treatment,
	// and the save-confirmation flag all agree.
	React.useEffect(() => {
		if (pendingUserMapEdits.size === 0) return;
		for (const row of allRows) {
			const entry = pendingUserMapEdits.get(row.providerId);
			if (entry && entry.newUserId === (row.userId ?? null)) {
				dropPendingUserMapEdit(row.providerId);
			}
		}
	}, [allRows, pendingUserMapEdits, dropPendingUserMapEdit]);

	const onAddEmployeeModalRequestClose = (result?: IAddEmployeeResult, cancel?: boolean) => {
		setShowAddEmployee(false);
		if (!cancel && !!result && !!result.user) {
			registerUserMutation.mutate({
				accountId: impersonationContext.account.id,
				sendWelcomeEmail: result.sendWelcomeEmail,
				user: result.user,
			});
		}
	};

	const onUserSelected = (
		e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.IUser>,
		row: Api.IIntegrationUser
	) => {
		setPendingUserMapEdit(row.providerId, e.selection.id, row.userId ?? null);
	};

	const clearLevitateUser = (row: Api.IIntegrationUser) => {
		setPendingUserMapEdit(row.providerId, null, row.userId ?? null);
	};

	const onCreateAutoCompleteViewModel = (
		_: Api.ResourceAutoCompleteViewModelType,
		suggestedViewModel: Api.ResourceAutoCompleteViewModel
	) => {
		suggestedViewModel.addParam({ includeDeactivated: false });
		return suggestedViewModel.impersonate({
			account: { id: impersonationContext.account.id },
		});
	};

	const onConnectionTypeChanged = (value: string) => setConnectionType(value);
	const onMappedStatusChanged = (value: string) => setMappedStatus(value as UserMapMappedStatus);

	const hasActiveFilters =
		searchInput.length > 0 || connectionType !== 'all' || mappedStatus !== UserMapMappedStatus.All;

	const clearFilters = () => {
		setSearchInput('');
		setConnectionType('all');
		setMappedStatus(UserMapMappedStatus.All);
	};

	// The export layout and its Levitate User column are specific to Northwestern Mutual's Dynamics org,
	// so the import stays hidden for every other broker dealer.
	const supportsSpreadsheetImport =
		integrationProvider === Api.IntegrationProvider.MicrosoftDynamics365Sales &&
		isNorthwesternMutualBrokerDealer(accountQuery.data);

	const showLoadMore = !isInitialLoading && hasNextPage;
	const showEmpty = !isInitialLoading && loadedCount === 0;

	return (
		<SettingGroup title='User Mappings'>
			<div className={css(styleSheet.subtitle)}>
				Manage user mappings for this integration to control which Levitate user is associated with each user in the
				external system. These mappings determine ownership of contacts, writebacks (if the integration supports it),
				and other data. Unmapped users may result in archived contacts or missing data. Changes to mappings will trigger
				a new data extract from the source system when you save.
			</div>
			<div className={css(styleSheet.actionBar)}>
				<div className={css(styleSheet.filterSection)}>
					<div className={css(styleSheet.filterGroup)}>
						<span className={css(styleSheet.filterLabel)}>Search:</span>
						<Input
							type='search'
							value={searchInput}
							onChange={e => setSearchInput(e.target.value)}
							placeholder='Search by name'
							styles={[styleSheet.searchInput]}
						/>
					</div>
					<div className={css(styleSheet.filterGroup)}>
						<span className={css(styleSheet.filterLabel)}>Connection Type:</span>
						<Select items={connectionTypeOptions} value={connectionType} onValueChange={onConnectionTypeChanged}>
							<SelectTrigger>
								<SelectValue placeholder='All Types' />
							</SelectTrigger>
							<SelectContent>
								{connectionTypeOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className={css(styleSheet.filterGroup)}>
						<span className={css(styleSheet.filterLabel)}>Status:</span>
						<Select items={mappedStatusOptions} value={mappedStatus} onValueChange={onMappedStatusChanged}>
							<SelectTrigger>
								<SelectValue placeholder='All Statuses' />
							</SelectTrigger>
							<SelectContent>
								{mappedStatusOptions.map(option => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<Button variant='secondary' size='sm' onClick={clearFilters} disabled={!hasActiveFilters}>
						Clear filters
					</Button>
				</div>

				<div className={css(styleSheet.buttonGroup)}>
					{supportsSpreadsheetImport ? (
						<EditModeButton displayValue='Import from spreadsheet' onClick={() => setShowImportUserMaps(true)} />
					) : null}
					<EditModeButton displayValue='Add Employee' onClick={() => setShowAddEmployee(true)} />
				</div>
			</div>

			<div className={css(styleSheet.tableContainer)}>
				<DataGrid styles={[styleSheet.tableOverrides]}>
					<DataGridColumns styles={[styleSheet.columnOverrides]}>
						<DataGridColumn sticky>Name</DataGridColumn>
						<DataGridColumn sticky>Connection Types</DataGridColumn>
						<DataGridColumn sticky>Levitate User</DataGridColumn>
					</DataGridColumns>

					<DataGridRows>
						{!isInitialLoading &&
							allRows.map(row => {
								const serverUserId = row.userId ?? null;
								const pendingEntry = pendingUserMapEdits.get(row.providerId);
								const hasPending = pendingEntry !== undefined;
								const pendingValue = pendingEntry ? pendingEntry.newUserId : null;
								const effectiveUserId = hasPending ? pendingValue : serverUserId;
								// Background refetch can catch the server value up to the pending value;
								// in that case the entry is a stale no-op and shouldn't render as edited.
								const isEdited = hasPending && pendingValue !== serverUserId;
								const levitateUser = effectiveUserId ? accountUsersById.get(effectiveUserId) : undefined;
								const hasResolvedUser = !!levitateUser;
								const hasUnresolvedId = !levitateUser && !!effectiveUserId;

								return (
									<DataGridRow key={`row-${row.providerId}`} styles={isEdited ? [styleSheet.modifiedRow] : undefined}>
										<DataGridRowItem>
											<div className={css(styleSheet.userNameCell)}>
												<div className={css(styleSheet.userNameContent)}>
													<h6 className={css(baseStyleSheet.truncateText, baseStyleSheet.m0)}>{row.displayName}</h6>
													{row.isActive === false && <Badge text='Deactivated' foregroundColor={warningDark} />}
												</div>
												<div className={css(styleSheet.statusIndicatorContainer)}>
													{isEdited && <span className={css(styleSheet.modifiedIndicator)}>(To be modified)</span>}
												</div>
											</div>
										</DataGridRowItem>
										<DataGridRowItem>
											{row.connectionTypes && row.connectionTypes.length > 0 ? (
												row.connectionTypes.map(type => (
													<Badge key={`badge-${type}`} text={type} foregroundColor='#9E9E9E' />
												))
											) : (
												<span className={css(styleSheet.emptyCell)}>—</span>
											)}
										</DataGridRowItem>
										<DataGridRowItem>
											<EditModeSimpleAutoCompleteSearchField
												displayValue={
													levitateUser ? getDisplayName(levitateUser) : hasUnresolvedId ? 'Unknown user' : '—'
												}
												type={Api.ResourceAutoCompleteViewModelType.User}
												// @ts-ignore
												onItemSelected={(e: ISimpleAutoCompleteSearchFieldItemSelectionEvent<Api.IUser>) =>
													onUserSelected(e, row)
												}
												dropdownContentItemsStyles={[styleSheet.autoCompleteDropdownItems]}
												dropdownContentStyle={styleSheet.autoCompleteDropdownContent}
												initialSearchQuery={levitateUser ? getDisplayName(levitateUser) : ''}
												onClear={() => clearLevitateUser(row)}
												rightAccessoryViewMode={AutoCompleteSearchFieldAccessoryViewMode.Always}
												style={styleSheet.userInput}
												textAlign='left'
												resultsLimit={5}
												pageSize={5}
												placeholder='Search name'
												onCreateAutoCompleteViewModel={onCreateAutoCompleteViewModel}
												customStyles={
													hasResolvedUser ? [styleSheet.readOnlyText] : [styleSheet.readOnlyText, styleSheet.emptyCell]
												}
											/>
										</DataGridRowItem>
									</DataGridRow>
								);
							})}

						{isInitialLoading ? (
							<DataGridRow>
								<DataGridRowItem fullColSpan>
									<LoadingSpinner type='large' />
								</DataGridRowItem>
							</DataGridRow>
						) : null}
					</DataGridRows>
				</DataGrid>
				{showEmpty ? <div className={css(styleSheet.emptyState)}>No user mappings match your filters</div> : null}
			</div>

			{!isInitialLoading && (loadedCount > 0 || pendingEditCount > 0) ? (
				<div className={css(styleSheet.footer)}>
					<span className={css(styleSheet.footerCount)}>
						{loadedCount > 0 ? `Showing ${loadedCount.toLocaleString()} of ${totalCount.toLocaleString()}` : null}
						{pendingEditCount > 0 ? (
							<span className={css(styleSheet.pendingEditsCount)}>
								{loadedCount > 0 ? ' · ' : ''}
								{pendingEditCount.toLocaleString()} unsaved {pendingEditCount === 1 ? 'change' : 'changes'} (saves on
								Save)
							</span>
						) : null}
					</span>
					{showLoadMore ? (
						<Button
							variant='secondary'
							size='sm'
							onClick={() => userMapsQuery.fetchNextPage()}
							isLoading={userMapsQuery.isFetchingNextPage}
							disabled={userMapsQuery.isFetchingNextPage}
						>
							Load more
						</Button>
					) : null}
				</div>
			) : null}

			{supportsSpreadsheetImport ? (
				<ImportUserMapsModal
					accountId={impersonationContext.account.id}
					usersById={accountUsersById}
					hasUnsavedMappingEdits={pendingEditCount > 0}
					isOpen={showImportUserMaps}
					onClose={() => setShowImportUserMaps(false)}
					onImported={() => invalidateInfiniteUserMapsQuery({ impersonationContext, integrationProvider })}
				/>
			) : null}

			<AddEmployeeModal
				modalProps={{
					isOpen: showAddEmployee,
					onRequestClose: onAddEmployeeModalRequestClose,
				}}
			/>
		</SettingGroup>
	);
};
